import React, { useEffect } from 'react';
import { Button, Typography, Spin, Card } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useBranding, useTheme } from '../../states/theme.atom';
const { Title, Text } = Typography;

interface LoginErrorCardProps {
  error: Error;
  onRetry: () => void;
  primaryColor: string;
  secondaryColor: string;
  theme: string;
}

interface LoginCardProps {
  isLoading: boolean;
  onLogin: () => void;
  primaryColor: string;
  secondaryColor: string;
  theme: string;
}

const Login: React.FC = () => {
  const { loginWithRedirect, isAuthenticated, isLoading, error } = useAuth0();
  const navigate = useNavigate();
  const { primaryColor, secondaryColor } = useBranding();
  const { theme } = useTheme();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/me', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = () => {
    loginWithRedirect({
      appState: {
        returnTo: '/me',
      },
    });
  };

  if (error) {
    return (
      <LoginErrorCard
        error={error}
        onRetry={handleLogin}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        theme={theme}
      />
    );
  }

  return (
    <LoginCard
      isLoading={isLoading}
      onLogin={handleLogin}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      theme={theme}
    />
  );
};

export default Login;

// Extracted UI components for clarity

const LoginErrorCard = ({
  error,
  onRetry,
  primaryColor,
  secondaryColor,
  theme,
}: LoginErrorCardProps) => (
  <div
    className="min-vh-100 d-flex align-items-center justify-content-center p-3"
    style={{
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
    }}
  >
    <Card
      className="w-100 border-0 shadow-lg"
      style={{
        maxWidth: '400px',
        borderRadius: '16px',
        backgroundColor: theme === 'dark' ? '#141414' : undefined,
      }}
      bodyStyle={{
        padding: '48px 32px',
        textAlign: 'center',
      }}
    >
      <div className="mb-4">
        <div
          className="mx-auto mb-4 d-flex align-items-center justify-content-center"
          style={{
            width: '64px',
            height: '64px',
            backgroundColor: '#ff4d4f',
            borderRadius: '50%',
            fontSize: '24px',
            color: 'white',
          }}
        >
          ⚠️
        </div>
        <Title level={3} className="mb-3" style={{ color: theme === 'dark' ? '#fff' : '#262626' }}>
          Authentication Error
        </Title>
        <Text
          className="d-block"
          style={{
            fontSize: '16px',
            color: theme === 'dark' ? '#bfbfbf' : '#8c8c8c',
            lineHeight: '1.5',
          }}
        >
          There was an error during authentication: {error.message}
        </Text>
      </div>
      <Button
        type="primary"
        size="large"
        onClick={onRetry}
        className="w-100"
        style={{
          height: '48px',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          border: 'none',
          boxShadow: `0 4px 12px ${primaryColor ? primaryColor + '4d' : 'rgba(102, 126, 234, 0.3)'}`,
        }}
      >
        Try Again
      </Button>
    </Card>
  </div>
);

const LoginCard = ({ isLoading, onLogin, primaryColor, secondaryColor, theme }: LoginCardProps) => (
  <div
    className="min-vh-100 d-flex align-items-center justify-content-center p-3"
    style={{
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
    }}
  >
    <Card
      className="w-100 border-0 shadow-lg"
      style={{
        maxWidth: '400px',
        borderRadius: '16px',
        backgroundColor: theme === 'dark' ? '#141414' : undefined,
      }}
      bodyStyle={{
        padding: '48px 32px',
        textAlign: 'center',
      }}
    >
      <div className="mb-4">
        <div
          className="mx-auto mb-4 d-flex align-items-center justify-content-center"
          style={{
            width: '64px',
            height: '64px',
            backgroundColor: primaryColor,
            borderRadius: '50%',
            fontSize: '24px',
            color: 'white',
          }}
        >
          🔐
        </div>
        <Title level={2} className="mb-3" style={{ color: theme === 'dark' ? '#fff' : '#262626' }}>
          Welcome back
        </Title>
        <Text
          className="d-block"
          style={{
            fontSize: '16px',
            color: theme === 'dark' ? '#bfbfbf' : '#8c8c8c',
            lineHeight: '1.5',
          }}
        >
          Access your dashboard by signing in with your credentials
        </Text>
      </div>

      <Button
        type="primary"
        size="large"
        onClick={onLogin}
        disabled={isLoading}
        className="w-100 d-flex align-items-center justify-content-center"
        style={{
          height: '48px',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          background: isLoading
            ? undefined
            : `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
          border: 'none',
          boxShadow: isLoading
            ? undefined
            : `0 4px 12px ${primaryColor ? primaryColor + '4d' : 'rgba(102, 126, 234, 0.3)'}`,
          gap: '8px',
        }}
      >
        {isLoading ? (
          <>
            <Spin size="small" />
            <span>Signing in...</span>
          </>
        ) : (
          'Continue with Auth0'
        )}
      </Button>

      <div className="mt-4">
        <Text style={{ fontSize: '14px', color: '#bfbfbf' }}>
          Secure authentication powered by Auth0
        </Text>
      </div>
    </Card>
  </div>
);
